import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { captureNonApiError } from '@utils/capture-non-api-error';

import type { ClauseFileImportOutcome } from '../types';
import { ClauseFileImportError, importClauseFile } from '../utils/import-clause-file';
import { validateImportFile } from '../utils/validate-import-file';
import type { Repairs } from '../utils/write/fit-to-schema';
import {
  showFormattingSimplifiedToast,
  showImportCancelledToast,
  showImportRejectedToast,
  showImportSuccessToast,
  showImportWarningToast,
} from '../views/ImportToasts';

type ImportSettlement = { status: 'cancelled' } | { status: 'applied'; repairs: Repairs; warnings: string[] };

class ClauseFileImportRepaired extends Error {
  name = 'ClauseFileImportRepaired';

  constructor(repairs: Repairs) {
    super(JSON.stringify(repairs));
  }
}

const settleImport = async (
  file: File,
  signal: AbortSignal,
  outcome: ClauseFileImportOutcome,
): Promise<ImportSettlement> => {
  try {
    const { doc, repairs, warnings } = await importClauseFile(await file.arrayBuffer(), {
      fileName: file.name,
      signal,
    });
    return outcome(doc) === 'applied' ? { status: 'applied', repairs, warnings } : { status: 'cancelled' };
  } catch (error) {
    if (signal.aborted) {
      return { status: 'cancelled' };
    }
    throw error;
  }
};

const reportSettlement = (settlement: ImportSettlement) => {
  if (settlement.status === 'cancelled') {
    showImportCancelledToast();
    return;
  }

  showImportSuccessToast();
  if (Object.keys(settlement.repairs).length > 0) {
    captureNonApiError(new ClauseFileImportRepaired(settlement.repairs));
    showFormattingSimplifiedToast();
  }
  if (settlement.warnings.length > 0) {
    showImportWarningToast(settlement.warnings);
  }
};

const reportFailure = (error: Error) =>
  showImportRejectedToast(error instanceof ClauseFileImportError ? error.failure : 'corrupt');

export const useClauseFileImport = (outcome: ClauseFileImportOutcome) => {
  const abortRef = useRef<AbortController>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ file, signal }: { file: File; signal: AbortSignal }) => settleImport(file, signal, outcome),
    onSuccess: reportSettlement,
    onError: reportFailure,
  });

  const importFile = (file: File, onApplied: () => void) => {
    const rejection = validateImportFile(file);
    if (rejection) {
      showImportRejectedToast(rejection);
      return;
    }

    abortRef.current = new AbortController();
    mutate(
      { file, signal: abortRef.current.signal },
      {
        onSuccess: ({ status }) => {
          if (status === 'applied') {
            onApplied();
          }
        },
      },
    );
  };

  return { importFile, isConverting: isPending };
};
